/**
 * Location data for the Multiconexão and Multi Internet cards (ported from the design's `app.js` §3).
 *
 * GEO_ENDPOINT: the site's geolocation endpoint (GET, JSON: { country, region, city, lat?, lon?, isp? },
 * alternate key names accepted). The ONE place to set it. Empty = no request: both cards use the US profile
 * (plan B) unless the URL forces a location for QA: `?mcgeo=BR|MG|Belo Horizonte&mcisp=Vivo`.
 * The countries / hubs / cities / regions / aliases / providers come from `geo-data.json`, loaded once
 * (a lazy chunk, so the 53 KB never sits in the page bundle); if it cannot be loaded, the built-in US
 * profile boots both cards.
 */

export const GEO_ENDPOINT = '';
const GEO_TIMEOUT = 2500;

/** [name, code, lat, lon] */
export type GeoCity = [string, string, number, number];
/** [country name, hub city name, cities] */
export type GeoCountry = [string, string, GeoCity[]];
export type GeoProviderFixed = [string, string];
export type GeoProviderMobile = [string, boolean];

export interface GeoData {
  countries: Record<string, GeoCountry>;
  regions: Record<string, Record<string, string>>;
  aliases: Record<string, string>;
  providers: Record<string, { fixed: GeoProviderFixed[]; mobile: GeoProviderMobile[] }>;
}

/** Raw answer of GEO_ENDPOINT (any of the usual key spellings). */
export type RawGeo = Record<string, unknown> | null;

export const NP_GEO_FALLBACK: GeoData = {
  countries: {
    US: [
      'Estados Unidos',
      'Chicago',
      [
        ['Chicago', 'CHI', 41.88, -87.63],
        ['Nova York', 'NYC', 40.71, -74.01],
        ['Washington', 'WAS', 38.9, -77.04],
        ['Atlanta', 'ATL', 33.75, -84.39],
        ['Miami', 'MIA', 25.76, -80.19],
        ['Dallas', 'DFW', 32.78, -96.8],
        ['Houston', 'HOU', 29.76, -95.37],
        ['Denver', 'DEN', 39.74, -104.99],
        ['Phoenix', 'PHX', 33.45, -112.07],
        ['Los Angeles', 'LAX', 34.05, -118.24],
        ['San Jose', 'SJC', 37.34, -121.89],
        ['Seattle', 'SEA', 47.61, -122.33],
        ['Kansas City', 'MCI', 39.1, -94.58],
        ['Minneapolis', 'MSP', 44.98, -93.27],
        ['Boston', 'BOS', 42.36, -71.06],
        ['Salt Lake City', 'SLC', 40.76, -111.89],
        ['Las Vegas', 'LAS', 36.17, -115.14],
        ['Detroit', 'DTW', 42.33, -83.05],
        ['Charlotte', 'CLT', 35.23, -80.84],
        ['St. Louis', 'STL', 38.63, -90.2],
        ['Filadélfia', 'PHL', 39.95, -75.17],
      ],
    ],
  },
  regions: {
    US: { AL: 'Atlanta', AK: 'Seattle', AZ: 'Phoenix', AR: 'Dallas', CA: 'Los Angeles', CO: 'Denver', CT: 'Nova York', DE: 'Washington', DC: 'Washington', FL: 'Miami', GA: 'Atlanta', HI: 'Los Angeles', ID: 'Salt Lake City', IL: 'Chicago', IN: 'Chicago', IA: 'Minneapolis', KS: 'Kansas City', KY: 'St. Louis', LA: 'Houston', ME: 'Boston', MD: 'Washington', MA: 'Boston', MI: 'Detroit', MN: 'Minneapolis', MS: 'Atlanta', MO: 'St. Louis', MT: 'Salt Lake City', NE: 'Kansas City', NV: 'Las Vegas', NH: 'Boston', NJ: 'Nova York', NM: 'Phoenix', NY: 'Nova York', NC: 'Charlotte', ND: 'Minneapolis', OH: 'Detroit', OK: 'Dallas', OR: 'Seattle', PA: 'Nova York', RI: 'Boston', SC: 'Charlotte', SD: 'Minneapolis', TN: 'Atlanta', TX: 'Dallas', UT: 'Salt Lake City', VT: 'Boston', VA: 'Washington', WA: 'Seattle', WV: 'Washington', WI: 'Chicago', WY: 'Denver' },
  },
  aliases: {},
  providers: {
    US: {
      fixed: [
        ['Spectrum', 'cable'],
        ['Xfinity', 'cable'],
        ['AT&T', 'fiber'],
        ['Verizon', 'fiber'],
        ['T-Mobile', 'wireless'],
      ],
      mobile: [
        ['Verizon', true],
        ['T-Mobile', true],
        ['AT&T', true],
      ],
    },
  },
};

let geoPromise: Promise<RawGeo> | null = null;

/** One request per page: both cards share it. Resolves `null` without an endpoint, on error or on timeout. */
export function sharedGeo(): Promise<RawGeo> {
  if (geoPromise) return geoPromise;
  geoPromise = new Promise<RawGeo>((resolve) => {
    if (!GEO_ENDPOINT || typeof fetch !== 'function') {
      resolve(null);
      return;
    }
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => {
      controller?.abort();
      resolve(null);
    }, GEO_TIMEOUT);
    fetch(GEO_ENDPOINT, { credentials: 'same-origin', signal: controller?.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then(
        (data) => {
          clearTimeout(timer);
          resolve(data as RawGeo);
        },
        () => {
          clearTimeout(timer);
          resolve(null);
        }
      );
  });
  return geoPromise;
}

function titleCase(text: string): string {
  return text.replace(/(^|[\s.'-])(\p{L})/gu, (_match, separator: string, letter: string) => separator + letter.toUpperCase());
}

/**
 * The data file speaks Portuguese: country names and a few exonyms ("Nova York", "Munique", "Cidade do México").
 * For every other locale the country name comes from `Intl.DisplayNames` and a Portuguese exonym becomes the
 * English name the alias table already knows ("new york" -> "Nova York" read backwards). Route codes, coordinates
 * and provider names never change. The alias table keeps resolving both spellings, so `?mcgeo=` works either way.
 */
export function localizeGeoData(data: GeoData, htmlLang: string): GeoData {
  if (htmlLang.toLowerCase().startsWith('pt')) return data;

  const english = new Map<string, string>();
  Object.entries(data.aliases).forEach(([alias, portugueseName]) => {
    if (!english.has(portugueseName)) english.set(portugueseName, titleCase(alias));
  });
  const cityName = (name: string) => english.get(name) ?? name;

  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([htmlLang, 'en'], { type: 'region' });
  } catch {
    displayNames = null;
  }
  const countryName = (code: string, fallback: string) => {
    try {
      return displayNames?.of(code) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const countries: Record<string, GeoCountry> = {};
  Object.entries(data.countries).forEach(([code, [name, hub, cities]]) => {
    countries[code] = [countryName(code, name), cityName(hub), cities.map(([city, cityCode, lat, lon]) => [cityName(city), cityCode, lat, lon] as GeoCity)];
  });
  const regions: Record<string, Record<string, string>> = {};
  Object.entries(data.regions).forEach(([code, map]) => {
    regions[code] = Object.fromEntries(Object.entries(map).map(([region, city]) => [region, cityName(city)]));
  });
  const aliases: Record<string, string> = Object.fromEntries(Object.entries(data.aliases).map(([alias, city]) => [alias, cityName(city)]));
  // the Portuguese spelling keeps resolving too (the design's QA links use it)
  english.forEach((englishName, portugueseName) => {
    const key = portugueseName
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9&]+/g, ' ')
      .trim();
    if (!aliases[key]) aliases[key] = englishName;
  });

  return { countries, regions, aliases, providers: data.providers };
}

const dataPromises = new Map<string, Promise<GeoData>>();

/**
 * geo-data.json, loaded once per locale and localised for it (see `localizeGeoData`). Never rejects:
 * anything that goes wrong yields the built-in US profile.
 */
export function loadGeoData(htmlLang: string): Promise<GeoData> {
  const cached = dataPromises.get(htmlLang);
  if (cached) return cached;
  const promise = import('./geo-data.json')
    .then((module) => {
      const data = ((module as { default?: unknown }).default ?? module) as GeoData;
      return data && data.countries && data.countries.US ? data : NP_GEO_FALLBACK;
    })
    .catch(() => NP_GEO_FALLBACK)
    .then((data) => localizeGeoData(data, htmlLang));
  dataPromises.set(htmlLang, promise);
  return promise;
}

/**
 * The host page mirrors the Multiconexão routes elsewhere (the "vantagem" mini panel, the AI routing card):
 * the card publishes its current model on `window.__npMcPlace` and dispatches `np:mc-place` on `window`.
 */
export interface McPlace {
  cc: string;
  hub: unknown;
  user: unknown;
  /** slots r1–r5 */
  routes: Array<{ city: string; code: string; cc?: string; lat?: number; lon?: number }>;
  /** per-scenario latency of each slot, plus the direct line */
  scenarios: Array<Record<string, number>>;
  [key: string]: unknown;
}

/* No `declare global` on purpose: other LPs augment `Window.__npMcPlace` with their own shape, and two global
   augmentations of one property do not merge. The two helpers below are the only readers and writers here. */
type McPlaceHost = { __npMcPlace?: McPlace };

export function readMcPlace(): McPlace | undefined {
  return typeof window === 'undefined' ? undefined : (window as unknown as McPlaceHost).__npMcPlace; // eslint-disable-line no-underscore-dangle
}

export function writeMcPlace(place: McPlace | undefined): void {
  if (typeof window === 'undefined') return;
  const host = window as unknown as McPlaceHost;
  if (place)
    host.__npMcPlace = place; // eslint-disable-line no-underscore-dangle
  else delete host.__npMcPlace; // eslint-disable-line no-underscore-dangle
}

export const MC_PLACE_EVENT = 'np:mc-place';
